import { BigNumber, ethers } from 'ethers';
import Toastr from 'toastr2';
import { constants, Invocations, TransactionType } from 'starknet';

import { ExplorerApi } from './explorer';

import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  setMinVersionModalVisible,
  setUpgradeModalVisible,
  setDeployModalVisible,
  setForceReconnectModalVisible,
} from 'slices/modalSlice';
import {
  setErc20TokenBalanceSelected,
  upsertErc20TokenBalance,
  setErc20TokenBalances,
  setAccounts,
  setCurrentAccount,
  setTransactions,
  setTransactionDeploy,
  setForceReconnect,
  setLocale,
  setTranslations,
  updateAccount,
  updateCurrentAccount,
  setWalletConnection,
  setTransferOffers,
} from 'slices/walletSlice';
import { setNetworksAndActiveNetwork } from 'slices/networkSlice';
import { disableLoading, enableLoadingWithMessage } from 'slices/UISlice';
import {
  Transaction,
  Account,
  Network,
  Erc20TokenBalance,
  Erc20Token,
} from 'types';
import {
  retry,
  getTokenBalanceWithDetails,
  isUserDenyError,
  shortenAddress,
} from 'utils/utils';
import { getAssetPriceUSD } from './coinGecko';
import { useSnap } from './useSnap';

export const useStarkNetSnap = () => {
  const dispatch = useAppDispatch();
  const { invokeSnap, isSnapRequireUpdate, requestSnap, ping } = useSnap();
  const { loader } = useAppSelector((state) => state.UI);
  const erc20TokenBalances = useAppSelector(
    (state) => state.wallet.erc20TokenBalances,
  );
  const accounts = useAppSelector((state) => state.wallet.accounts);

  const connectToSnap = async () => {
    dispatch(enableLoadingWithMessage('Connecting...'));
    try {
      await requestSnap();
      dispatch(setWalletConnection(true));
      dispatch(setForceReconnect(false));
    } catch (error) {
      dispatch(setWalletConnection(false));
    } finally {
      dispatch(disableLoading());
    }
  };

  const checkConnection = async () => {
    dispatch(enableLoadingWithMessage('checking connection...'));
    try {
      await ping();
      dispatch(setWalletConnection(true));
    } catch (error) {
      dispatch(setWalletConnection(false));
    } finally {
      dispatch(disableLoading());
    }
  };

  const loadLocale = async () => {
    try {
      const { locale } = await invokeSnap<{
        locale: string;
      }>({
        method: 'canton_getPreferences',
      });
      const messages = await import(`../assets/locales/${locale}.json`);
      dispatch(setLocale(locale));
      dispatch(setTranslations(messages.messages));
    } catch (error) {
      console.error(
        'Failed to load user preferences. Falling back to default locale.',
        error,
      );
    }
  };

  const getNetworks = async () => {
    return await invokeSnap<Network[]>({
      method: 'canton_getStoredNetworks',
      params: {},
    });
  };

  const getTokens = async (chainId: string) => {
    return await invokeSnap<Erc20Token[]>({
      method: 'canton_getStoredErc20Tokens',
      params: {
        chainId,
      },
    });
  };

  const getAccounts = async (chainId: string) => {
    return await invokeSnap<Account[]>({
      method: 'canton_listAccounts',
      params: {
        chainId,
      },
    });
  };

  const requestUpgradeSnap = () => {
    dispatch(setMinVersionModalVisible(true));
  };

  const completeUpgradeSnap = async () => {
    dispatch(setMinVersionModalVisible(false));
    await connectToSnap();
    await initSnap();
  };

  const initSnap = async () => {
    if (await isSnapRequireUpdate()) {
      requestUpgradeSnap();
      return;
    }

    if (!loader.isLoading) {
      dispatch(enableLoadingWithMessage('Initializing wallet ...'));
    }
    try {
      await loadLocale();

      const networks = await getNetworks();
      if (networks.length === 0) {
        console.error('No networks found');
        return;
      }

      console.log('networks', networks);

      const currentNetwork = await getCurrentNetwork();
      const idx = networks.findIndex(
        (network) => network.chainId === currentNetwork.chainId,
      );

      console.log('idx', idx);

      dispatch(
        setNetworksAndActiveNetwork({
          networks,
          activeNetwork: idx,
        }),
      );

      console.log('calling initWalletData...');

      await initWalletData({
        chainId: currentNetwork.chainId,
      });

      console.log('wallet initialized!');
    } catch (err: any) {
      if (err.code && err.code === 4100) {
        const toastr = new Toastr();
        toastr.error('Snap is unaccessible or unauthorized');
        dispatch(setWalletConnection(false));
      }
      if (err.code && err.code === -32603) {
        //We have to make the user reinstall the snap after the flask update to 10.25.0
        //following the breaking change : snap_manageState now uses SIP-6 algorithm for encryption
        //This change breaks the old snap state, hence the reinstallation
        dispatch(setForceReconnectModalVisible(true));
      }
      //eslint-disable-next-line no-console
      console.error('Error while Initializing wallet', err);
    } finally {
      dispatch(disableLoading());
    }
  };

  const initWalletData = async ({
    account,
    chainId,
  }: {
    account?: Account;
    chainId: string;
  }) => {
    if (!loader.isLoading) {
      dispatch(enableLoadingWithMessage('Getting network data ...'));
    }

    let currentAccount = account;
    if (!currentAccount) {
      console.log('getting current account...');
      currentAccount = await getCurrentAccount(chainId);
    }

    await setAccount(chainId, currentAccount);

    const { address } = currentAccount;

    console.log('getting balances...');

    await initTokensAndBalances(chainId, address);

    dispatch(disableLoading());
  };

  const setAccount = async (chainId: string, currentAccount: Account) => {
    const {
      upgradeRequired,
      isDeployed,
      transferPreapproved,
      mergeDelegationCreated,
    } = currentAccount;

    console.log('setAccount() isDeployed', isDeployed);
    console.log('setAccount()transferPreapproved', transferPreapproved);
    console.log('setAccount() mergeDelegationCreated', mergeDelegationCreated);

    dispatch(setCurrentAccount(currentAccount));

    // if no accounts from state, we fetch the accounts from snap
    if (!accounts || accounts.length === 0) {
      const accounts = await getAccounts(chainId);
      dispatch(setAccounts(accounts));
    }

    // TODO: hardcode to set the info modal visible,
    // but it should only visible when the account is not deployed
    // dispatch(setInfoModalVisible(true));
    dispatch(setUpgradeModalVisible(upgradeRequired));
    dispatch(
      setDeployModalVisible(
        isDeployed === false ||
          transferPreapproved === false ||
          mergeDelegationCreated === false,
      ),
    );
    // dispatch(setDeployModalVisible(true));
  };

  const setErc20TokenBalance = (erc20TokenBalance: Erc20TokenBalance) => {
    dispatch(setErc20TokenBalanceSelected(erc20TokenBalance));
  };

  const instrumentKey = (
    x: Pick<Erc20Token, 'chainId' | 'instrumentAdmin' | 'id'>,
  ) => `${x.chainId}::${x.instrumentAdmin}::${x.id}`;

  const toErc20Token = (x: any): Erc20Token => ({
    id: x.id,
    name: x.name ?? x.id, // fallback
    symbol: x.symbol ?? x.id, // fallback
    decimals: x.decimals ?? 10, // fallback
    instrumentAdmin: x.instrumentAdmin,
    chainId: x.chainId,
  });

  const initTokensAndBalances = async (chainId: string, address: string) => {
    const tokensRaw = await getTokens(chainId);
    const allBalances = await getAllBalances(address, chainId);

    // normalize base tokens
    const tokens: Erc20Token[] = tokensRaw.map(toErc20Token);

    // balance lookup by key
    const balancesByKey = new Map<string, string>(
      allBalances.balances.map((b: any) => [
        instrumentKey(toErc20Token(b.instrument)),
        b.balance,
      ]),
    );

    // merged instruments: tokens first, then append missing from balances
    const merged: Erc20Token[] = [...tokens];
    const seen = new Set(tokens.map(instrumentKey));

    for (const b of allBalances.balances) {
      const inst = toErc20Token(b.instrument);
      const key = instrumentKey(inst);
      if (!seen.has(key)) {
        merged.push(inst);
        seen.add(key);
      }
    }

    const tokensWithBalances: Erc20TokenBalance[] = await Promise.all(
      merged.map(async (token) => {
        const rawBalStr = balancesByKey.get(instrumentKey(token)) ?? '0';
        // ethers.utils.parseUnits doesn't support scientific notation (e.g. "1e-7"),
        // so convert only those to fixed decimal strings.
        const balStr = rawBalStr.includes('e')
          ? Number(rawBalStr).toFixed(token.decimals)
          : rawBalStr;
        const balance = {
          balance: ethers.utils.parseUnits(balStr, token.decimals),
        };

        const usdPrice = await getAssetPriceUSD(token);
        return getTokenBalanceWithDetails(balance, token, usdPrice);
      }),
    );

    dispatch(setErc20TokenBalances(tokensWithBalances));
    if (tokensWithBalances.length > 0)
      setErc20TokenBalance(tokensWithBalances[0]);
  };

  async function getPrivateKeyFromAddress(address: string, chainId: string) {
    try {
      await invokeSnap<null>({
        method: 'canton_displayPrivateKey',
        params: {
          address: address,
          chainId,
        },
      });
    } catch (error) {
      if (!isUserDenyError(error)) {
        throw error;
      }
    }
  }

  async function estimateFees(
    contractAddress: string,
    contractFuncName: string,
    contractCallData: string,
    address: string,
    chainId: string,
    transactionVersion?: typeof constants.TRANSACTION_VERSION.V3,
  ) {
    const invocations: Invocations = [
      {
        type: TransactionType.INVOKE,
        payload: {
          contractAddress,
          entrypoint: contractFuncName,
          calldata: contractCallData.split(',').map((ele) => ele.trim()),
        },
      },
    ];

    return await invokeSnap<{
      fee: string;
    }>({
      method: 'canton_estimateFee',
      params: {
        address,
        invocations,
        details: { version: transactionVersion },
        chainId,
      },
    });
  }

  async function createTransfer(
    address: string,
    recipient: string,
    amount: string,
    memo: string,
    chainId: string,
    instrumentAdmin: string,
    instrumentId: string,
  ) {
    dispatch(enableLoadingWithMessage('Creating transfer command...'));
    try {
      const response = await invokeSnap<{
        command: any;
        disclosedContracts: any;
        preparedTx: any;
      }>({
        method: 'canton_createTransfer',
        params: {
          address,
          recipient,
          amount,
          memo,
          chainId,
          instrumentAdmin,
          instrumentId,
        },
      });

      return response;
    } catch (error) {
      throw error;
    } finally {
      dispatch(disableLoading());
    }
  }

  async function getOffers(address: string, chainId: string) {
    // dispatch(enableLoadingWithMessage('Getting transfer offers...'));
    try {
      const response = await invokeSnap<{
        offers: any[];
      }>({
        method: 'canton_getOffers',
        params: {
          address,
          chainId,
        },
      });

      // Normalize offers array
      const offers = Array.isArray(response)
        ? response
        : response?.offers ?? [];
      dispatch(setTransferOffers(offers));
      return offers;
    } catch (error) {
      dispatch(setTransferOffers([]));
      throw error;
    } finally {
      // dispatch(disableLoading());
    }
  }

  async function sendTransaction(
    address: string,
    command: any,
    preparedTx: any,
    disclosedContracts: any,
    maxFee: string,
    chainId: string,
    feeToken: string,
  ) {
    dispatch(enableLoadingWithMessage('Sending transaction...'));
    console.log('usesnap.ts sendTransaction()...');
    try {
      const response = await invokeSnap<{
        transaction_hash: string;
      }>({
        method: 'canton_executeTxn',
        params: {
          address,
          command,
          preparedTx,
          // disclosedContracts,
          chainId,
        },
      });

      // Reload balances
      await initTokensAndBalances(chainId, address);
      // Reload transactions
      await getTransactions(address, address, 20, chainId, false);
      // Reload offers
      await getOffers(address, chainId);

      return response;
    } catch (error) {
      if (!isUserDenyError(error)) {
        throw error;
      }
    } finally {
      dispatch(disableLoading());
    }
  }

  const getTransactionStatus = async (
    transactionHash: string,
    chainId: string,
  ) => {
    return await invokeSnap<{
      executionStatus?: string;
      finalityStatus?: string;
    }>({
      method: 'canton_getTransactionStatus',
      params: {
        transactionHash,
        chainId,
      },
    });
  };

  const readContract = async <Resp>(
    contractAddress: string,
    contractFuncName: string,
  ) => {
    return await invokeSnap<Resp>({
      method: 'canton_getValue',
      params: {
        contractAddress,
        contractFuncName,
      },
    });
  };

  const deployAccount = async (
    partyId: string,
    maxFee: string,
    chainId: string,
    addressIndex: number,
    inviteCode: string,
  ) => {
    dispatch(enableLoadingWithMessage('Deploying account...'));
    try {
      const response = await invokeSnap<{
        updateId: string;
      }>({
        method: 'canton_deployAccount',
        params: {
          partyId,
          maxFee,
          chainId,
          deploy: true,
          addressIndex,
          inviteCode,
        },
      });

      console.log('deployAccount() response', response);
      console.log(typeof response);
      return response;
    } catch (error) {
      console.error(error);
      if (!isUserDenyError(error)) {
        throw error;
      }
      return false;
    } finally {
      dispatch(disableLoading());
    }
  };

  const upgradeAccount = async (
    contractAddress: string,
    maxFee: string,
    chainId: string,
  ) => {
    dispatch(enableLoadingWithMessage('Upgrading account...'));
    try {
      const response = await invokeSnap<{
        transaction_hash: string;
      }>({
        method: 'canton_upgradeAccContract',
        params: {
          contractAddress,
          maxFee,
          chainId,
        },
      });
      return response;
    } catch (error) {
      if (!isUserDenyError(error)) {
        throw error;
      }
      return false;
    } finally {
      dispatch(disableLoading());
    }
  };

  const getTransactions = async (
    senderAddress: string,
    contractAddress: string,
    txnsInLastNumOfDays: number,
    chainId: string,
    showLoading: boolean = true,
    onlyFromState: boolean = false,
  ) => {
    console.log('getTransactions()', senderAddress);
    if (showLoading) {
      dispatch(enableLoadingWithMessage('Retrieving transactions...'));
    }

    try {
      // const data = await invokeSnap<Array<Transaction>>({
      //   method: 'canton_getTransactions',
      //   params: {
      //     senderAddress,
      //     contractAddress,
      //     txnsInLastNumOfDays,
      //     chainId,
      //   },
      // });
      const data = await ExplorerApi.getUpdates(senderAddress, 20);

      console.log('data', data);

      let storedTxns = data.updates;

      //Set the deploy transaction
      const deployTransaction = storedTxns.find(
        (txn: Transaction) =>
          txn.txnType === TransactionType.DEPLOY ||
          txn.txnType === TransactionType.DEPLOY_ACCOUNT,
      );

      console.log('deployTransaction', deployTransaction);

      dispatch(setTransactionDeploy(deployTransaction));

      dispatch(setTransactions(storedTxns));

      return data;
    } catch (error) {
      dispatch(setTransactions([]));
    } finally {
      if (showLoading) {
        dispatch(disableLoading());
      }
    }
  };

  const addErc20Token = async (
    tokenAddress: string,
    tokenName: string,
    tokenSymbol: string,
    tokenDecimals: number,
    chainId: string,
    accountAddress: string,
  ) => {
    dispatch(enableLoadingWithMessage('Adding Token...'));
    try {
      await invokeSnap<null>({
        method: 'canton_addErc20Token',
        params: {
          tokenAddress,
          tokenName,
          tokenSymbol,
          tokenDecimals,
          chainId,
        },
      });

      const token = {
        instrumentAdmin: tokenAddress,
        name: tokenName,
        symbol: tokenSymbol,
        decimals: tokenDecimals,
        chainId,
        id: '',
      };

      const tokenBalance = await getTokenBalance(
        tokenAddress,
        accountAddress,
        chainId,
      );

      const usdPrice = await getAssetPriceUSD(token);
      const tokenWithBalance: Erc20TokenBalance = getTokenBalanceWithDetails(
        tokenBalance,
        token,
        usdPrice,
      );
      dispatch(upsertErc20TokenBalance(tokenWithBalance));
      return tokenWithBalance;
    } catch (error) {
      if (!isUserDenyError(error)) {
        throw error;
      }
      return null;
    } finally {
      dispatch(disableLoading());
    }
  };

  const updateTokenBalance = async (
    tokenAddress: string,
    accountAddress: string,
    chainId: string,
  ) => {
    console.log('updateTokenBalance()', tokenAddress, accountAddress, chainId);
    console.log('updateTokenBalance() erc20TokenBalances', erc20TokenBalances);
    const foundTokenWithBalance = erc20TokenBalances.find(
      (tokenBalance) =>
        tokenBalance.instrumentAdmin === tokenAddress &&
        tokenBalance.chainId === chainId,
    );
    if (foundTokenWithBalance) {
      const tokenBalance = await getTokenBalance(
        tokenAddress,
        accountAddress,
        chainId,
      );
      const usdPrice = await getAssetPriceUSD(foundTokenWithBalance);
      const tokenWithBalance: Erc20TokenBalance = getTokenBalanceWithDetails(
        tokenBalance,
        foundTokenWithBalance,
        usdPrice,
      );
      dispatch(upsertErc20TokenBalance(tokenWithBalance));
    }
  };

  const getTokenBalance = async (
    tokenAddress: string,
    userAddress: string,
    chainId: string,
  ) => {
    try {
      const response = await invokeSnap<{
        balance: string;
      }>({
        method: 'canton_getErc20TokenBalance',
        params: {
          tokenAddress,
          userAddress,
          chainId,
        },
      });

      console.log('getTokenBalance() response', response);
      return {
        balance: BigNumber.from(ethers.utils.parseUnits(response.balance, 10)),
      };
    } catch (error) {
      console.error('error getting token balance', error);
      return {
        balance: BigNumber.from('0'),
      };
    }
  };

  const getAllBalances = async (userAddress: string, chainId: string) => {
    try {
      const response = await invokeSnap<{
        partyId: string;
        balances: any[];
      }>({
        method: 'canton_getAllBalances',
        params: {
          userAddress,
          chainId,
        },
      });

      console.log('getAllBalances() response', response);
      return response;
    } catch (error) {
      console.error('error getting all balances', error);
      return {
        partyId: userAddress,
        balances: [],
      };
    }
  };

  const refreshTokensUSDPrice = async () => {
    console.log('refreshTokensUSDPrice()', erc20TokenBalances);
    if (erc20TokenBalances.length > 0) {
      const tokenUSDPrices = await Promise.all(
        erc20TokenBalances.map(async (token) => {
          return await getAssetPriceUSD(token);
        }),
      );

      console.log('tokenUSDPrices', tokenUSDPrices);

      const tokensRefreshed = erc20TokenBalances.map(
        (token, index): Erc20TokenBalance => {
          return {
            ...token,
            usdPrice: tokenUSDPrices[index],
          };
        },
      );

      console.log('tokensRefreshed', tokensRefreshed);
      dispatch(setErc20TokenBalances(tokensRefreshed));
    }
  };

  const waitForTransaction = async (
    transactionHash: string,
    chainId: string,
  ) => {
    let txStatus;
    const successStates = ['ACCEPTED_ON_L2', 'ACCEPTED_ON_L1'];
    const errorStates = ['REJECTED', 'NOT_RECEIVED'];

    const executeFn = async () => {
      txStatus = await getTransactionStatus(transactionHash, chainId);

      if (
        !txStatus ||
        !('executionStatus' in txStatus) ||
        !('finalityStatus' in txStatus)
      ) {
        return false;
      }

      if (
        txStatus.finalityStatus &&
        successStates.includes(txStatus.finalityStatus)
      ) {
        return true;
      } else if (
        txStatus.executionStatus &&
        errorStates.includes(txStatus.executionStatus)
      ) {
        const message = txStatus.executionStatus;
        throw new Error(message);
      }

      return false;
    };

    await retry(executeFn);

    return txStatus;
  };

  // const waitForAccountCreation = async (
  //   transactionHash: string,
  //   accountAddress: string,
  //   chainId: string,
  // ) => {
  //   dispatch(
  //     enableLoadingWithMessage('Waiting for transaction to be finalised.'),
  //   );
  //   const toastr = new Toastr();
  //   let result = false;

  //   try {
  //     // read transaction to check if the txn is ready
  //     await waitForTransaction(transactionHash, chainId);
  //   } catch (e) {
  //     //eslint-disable-next-line no-console
  //     console.log(`error while wait for transaction: ${e}`);
  //   }

  //   try {
  //     const executeFn = async (): Promise<boolean> => {
  //       // read contract to check if upgrade is required
  //       const resp = await readContract<string[]>(accountAddress, 'getVersion');
  //       if (!resp || !resp[0]) {
  //         return false;
  //       }

  //       return true;
  //     };

  //     result = await retry(executeFn, {
  //       maxAttempts: 20,
  //     });
  //   } catch (e: any) {
  //     //eslint-disable-next-line no-console
  //     console.log(`error while processing waitForAccountDeploy: ${e}`);
  //     toastr.error('Snap is unable to verify the contract deploy process');
  //   }

  //   dispatch(disableLoading());

  //   return result;
  // };

  // const waitForAccountUpdate = async (
  //   transactionHash: string,
  //   accountAddress: string,
  //   chainId: string,
  // ) => {
  //   dispatch(
  //     enableLoadingWithMessage('Waiting for transaction to be finalised.'),
  //   );
  //   const toastr = new Toastr();
  //   let result = false;

  //   try {
  //     // read transaction to check if the txn is ready
  //     await waitForTransaction(transactionHash, chainId);
  //   } catch (e) {
  //     //eslint-disable-next-line no-console
  //     console.log(`error while wait for transaction: ${e}`);
  //   }

  //   try {
  //     const executeFn = async (): Promise<boolean> => {
  //       // read contract to check if upgrade is required
  //       const resp = await readContract<string[]>(accountAddress, 'getVersion');
  //       if (!resp || !resp[0]) {
  //         return false;
  //       }

  //       if (!isGTEMinVersion(hexToString(resp[0]))) {
  //         return false;
  //       }

  //       return true;
  //     };

  //     result = await retry(executeFn, {
  //       maxAttempts: 20,
  //     });
  //   } catch (e: any) {
  //     //eslint-disable-next-line no-console
  //     console.log(`error while processing waitForAccountUpdate: ${e}`);
  //     toastr.error('Snap is unable to verify the contract upgrade process');
  //   }

  //   dispatch(disableLoading());

  //   return result;
  // };

  const switchNetwork = async (chainId: string) => {
    dispatch(enableLoadingWithMessage('Switching Network...'));
    try {
      return await invokeSnap<boolean>({
        method: 'canton_switchNetwork',
        params: {
          chainId,
        },
      });
    } catch (error) {
      dispatch(disableLoading());
      return false;
    }
  };

  const getCurrentNetwork = async () => {
    return await invokeSnap<Network>({
      method: 'canton_getCurrentNetwork',
    });
  };

  // const getStarkName = async (userAddress: string, chainId: string) => {
  //   return await invokeSnap<string>({
  //     method: 'canton_getStarkName',
  //     params: {
  //       userAddress,
  //       chainId,
  //     },
  //   });
  // };

  const getAddrFromStarkName = async (starkName: string, chainId: string) => {
    return await invokeSnap<string>({
      method: 'canton_getAddrFromStarkName',
      params: {
        starkName,
        chainId,
      },
    });
  };

  const addNewAccount = async (chainId: string, accountName?: string) => {
    dispatch(enableLoadingWithMessage('Adding new account...'));
    try {
      const account = await invokeSnap<Account>({
        method: 'canton_addAccount',
        params: {
          chainId,
          accountName,
        },
      });

      await initWalletData({
        account,
        chainId,
      });

      // push the current account into state
      dispatch(setAccounts(account));

      return account;
    } catch (err: any) {
      const toastr = new Toastr();
      toastr.error(err.message as unknown as string);
    } finally {
      dispatch(disableLoading());
    }
  };

  const getCurrentAccount = async (chainId: string) => {
    return await invokeSnap<Account>({
      method: 'canton_getCurrentAccount',
      params: {
        chainId,
      },
    });
  };

  const toggleAccountVisibility = async (
    chainId: string,
    address: string,
    visibility: boolean,
  ) => {
    return await invokeSnap<Account>({
      method: 'canton_toggleAccountVisibility',
      params: {
        chainId,
        address,
        visibility,
      },
    });
  };

  const updateAccountName = async (
    chainId: string,
    address: string,
    accountName: string,
  ) => {
    try {
      dispatch(enableLoadingWithMessage('Changing Account Name...'));
      await invokeSnap<Account>({
        method: 'canton_setAccountName',
        params: {
          chainId,
          address,
          accountName,
        },
      });
      dispatch(updateAccount({ address, updates: { accountName } }));
      dispatch(updateCurrentAccount({ accountName }));
    } catch (err: any) {
      const toastr = new Toastr();
      toastr.error(err.message as unknown as string);
    } finally {
      dispatch(disableLoading());
    }
  };

  const switchAccount = async (chainId: string, address: string) => {
    dispatch(
      enableLoadingWithMessage(
        `Switching Account to ${shortenAddress(address)}`,
      ),
    );
    try {
      const account = await invokeSnap<Account>({
        method: 'canton_switchAccount',
        params: {
          chainId,
          address,
        },
      });

      await initWalletData({
        account,
        chainId,
      });

      return account;
    } catch (err: any) {
      const toastr = new Toastr();
      toastr.error(err.message as unknown as string);
    } finally {
      dispatch(disableLoading());
    }
  };

  return {
    completeUpgradeSnap,
    connectToSnap,
    loadLocale,
    getNetworks,
    getAccounts,
    switchAccount,
    getCurrentAccount,
    addNewAccount,
    toggleAccountVisibility,
    updateAccountName,
    setAccount,
    setErc20TokenBalance,
    getPrivateKeyFromAddress,
    estimateFees,
    createTransfer,
    getOffers,
    sendTransaction,
    upgradeAccount,
    deployAccount,
    getTransactions,
    getTransactionStatus,
    waitForTransaction,
    // waitForAccountUpdate,
    // waitForAccountCreation,
    updateTokenBalance,
    getTokenBalance,
    addErc20Token,
    getTokens,
    checkConnection,
    initSnap,
    initWalletData,
    refreshTokensUSDPrice,
    readContract,
    switchNetwork,
    getCurrentNetwork,
    // getStarkName,
    getAddrFromStarkName,
  };
};
